import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getRectFromElement, getRectElementPositionAsync } from 'libs/positions';
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import deepEqual from 'fast-deep-equal';
import { useSession } from 'components/Authenticated';
import { nanoid } from 'nanoid';
import DocumentWidget from '../DocumentWidget';
import SectionTag from '../DocumentWidget/SectionTag';
import useResizeObserver from '../../MediaPlayer/use-resize-observer';
import SpeakerWidget from '../DocumentWidget/SpeakerWidget';
const MemoizedDocumentWidgets = ({ editorController, isRightToLeft, isCaptionMode, onClick, disabled, editorRef, }) => {
    const { session } = useSession();
    const widgetsRef = useRef(null);
    const speakerNodes = editorController.speakers.getSpeakerNodes();
    const sectionNodes = editorController.sections.getSectionNodes();
    const [visibleNodes, setVisibleNodes] = useState(new Set());
    const visibleSpeakerNodes = Array.from(speakerNodes).filter((node) => visibleNodes.has(node));
    const [positions, setPositions] = useState({
        speakers: [],
        scrollOffset: 0,
    });
    const uniqueSpeakerNodes = Array.from(speakerNodes)
        .map((node) => node.className)
        .join('|');
    useEffect(() => {
        const editorElement = editorRef.current;
        const observerCallback = (entries) => {
            setVisibleNodes((prevVisibleNodes) => {
                const newVisibleNodes = new Set(prevVisibleNodes);
                entries.forEach((entry) => {
                    const targetNode = entry.target;
                    if (entry.isIntersecting) {
                        newVisibleNodes.add(targetNode);
                    }
                    else {
                        newVisibleNodes.delete(targetNode);
                    }
                });
                return newVisibleNodes;
            });
        };
        const observer = new IntersectionObserver(observerCallback, {
            root: editorElement,
            threshold: 0.5,
        });
        const allNodes = [...Array.from(speakerNodes), ...Array.from(sectionNodes)];
        allNodes.forEach((node) => {
            observer.observe(node);
        });
        return () => {
            allNodes.forEach((node) => {
                observer.unobserve(node);
            });
            observer.disconnect();
        };
        // NOTE: We need to reset observing when adding or deleting or replacing nodes as well as
        // when toggling captions - that overrides all previous nodes with new ones.
    }, [editorRef, uniqueSpeakerNodes, sectionNodes.length,
        editorController.captions.enabled]);
    const isOnlyScrollDifference = (prevPosition, currentPosition, editorElement) => {
        if (prevPosition === undefined) {
            return false;
        }
        const previousRelativePos = prevPosition.top - editorElement.offsetTop;
        const currentRelativePos = currentPosition.top - editorElement.offsetTop;
        const roundedPosDifference = Math.round((currentRelativePos - previousRelativePos) * 10) / 10;
        const roundedScrollDifference = Math.round((positions.scrollOffset - editorElement.scrollTop) * 10) / 10;
        if (roundedPosDifference === roundedScrollDifference) {
            return true;
        }
        return false;
    };
    const setNewPositions = () => {
        if (editorRef.current === null) {
            return;
        }
        setPositions({
            speakers: getRectElementPositionAsync(speakerNodes),
            scrollOffset: editorRef.current.scrollTop,
        });
    };
    const handlePositionsUpdate = useCallback((event) => {
        const allSpeakers = Array.from(speakerNodes);
        const speakersBeginIndex = allSpeakers.map((node) => editorController.speakers.getSpeakerIndexRange(node).begin);
        const closestHighestSpeakerIndex = speakersBeginIndex.findIndex((index) => index >= event.editedFrom);
        // NOTE: No speaker found or editor not initialized.
        if (closestHighestSpeakerIndex === -1 || editorRef.current === null) {
            return;
        }
        const closestSpeakerPosition = getRectFromElement(allSpeakers[closestHighestSpeakerIndex]);
        const prevPosition = positions.speakers[closestHighestSpeakerIndex];
        if (deepEqual(prevPosition, closestSpeakerPosition) === false) {
            if (isOnlyScrollDifference(prevPosition, closestSpeakerPosition, editorRef.current)) {
                return;
            }
            setNewPositions();
        }
    }, [visibleSpeakerNodes]);
    const handleWidgetsChanged = () => {
        setNewPositions();
    };
    useResizeObserver({
        callback: setNewPositions,
        elementRef: widgetsRef,
    });
    useEffect(() => {
        handlePositionsUpdate({ editedFrom: 0, editedTo: 0 });
    }, [session.login.user.settings.editorFontDelta]);
    useEffect(() => {
        editorController.addEventListener('text-changed', handlePositionsUpdate);
        editorController.addEventListener('widget-changed', handleWidgetsChanged);
        return () => {
            editorController.removeEventListener('text-changed', handlePositionsUpdate);
            editorController.removeEventListener('widget-changed', handleWidgetsChanged);
        };
    }, [handlePositionsUpdate]);
    const generateSectionTags = () => {
        if (!editorController.areSectionTagsAllowed) {
            return null;
        }
        return Array.from(sectionNodes)
            .filter((node) => visibleNodes.has(node))
            .map((node) => {
            var _a;
            const key = (_a = node.textContent) === null || _a === void 0 ? void 0 : _a.substring(0, 30);
            return (_jsx(DocumentWidget, { WidgetComponent: SectionTag, widgetTrigger: node, onClick: onClick, disabled: disabled, editorController: editorController, isRightToLeft: isRightToLeft, isCaptionMode: isCaptionMode }, key));
        });
    };
    const generateSpeakerWidgets = () => {
        return visibleSpeakerNodes
            .map((node) => (_jsx("div", { children: _jsx(DocumentWidget, { WidgetComponent: SpeakerWidget, widgetTrigger: node, onClick: onClick, disabled: disabled, editorController: editorController, isCaptionMode: isCaptionMode, isRightToLeft: isRightToLeft }) }, nanoid(5))));
    };
    return (_jsxs("div", { ref: widgetsRef, children: [generateSectionTags(), generateSpeakerWidgets()] }));
};
export default React.memo(MemoizedDocumentWidgets);
